import {
    Component,
    Input,
    forwardRef,
    EventEmitter,
    Output,
    OnInit,
    OnChanges,
    SimpleChanges,
    OnDestroy
} from '@angular/core';

import { Subscription, BehaviorSubject } from 'rxjs';
import { filter } from 'rxjs/operators';

import { AbstractBaseStyleFieldComponent } from '../../BaseStyleField/abstract-base-style-field.component';
import { AbstractPageBuilderService } from '../../../../../Common/PageBuilder/services/abstract-page-builder.service';
import { ButtonTypesEnum } from '../../../../../Common/Forms/enums/button-types.enum';
import { CoreDialogService } from '../../../../../Core/Dialogs/services/dialog.service';
import { DividerStyleFieldModel } from '../../../models/divider-style-field.model';
import { DividerStyleModel } from '../../../models/divider-style.model';
import { DropdownFieldModel } from '../../../../../Common/PageBuilder/models/dropdown-field.model';
import { DropdownOptionModel } from '../../../../../Common/Forms/models/dropdown-option.model';
import { ManageStylesDialogComponent } from '../../../../../Common/Styles/components/ManageStylesDialog/manage-styles-dialog.component';
import { messageSeverityEnum } from '../../../../../Common/Misc/enums/message-severity.enum';
import { SaveStylesDialogComponent } from '../../../../../Common/Dialogs/components/SaveStylesDialog/save-styles-dialog.component';
import { SaveTwigTemplateDialogComponent } from '../../../../../Common/Dialogs/components/SaveTwigTemplateDialog/save-twig-template-dialog.component';
import { Style } from '../../../../../Common/Styles/models/style.model';
import { StylesService } from '../../../../../Common/Styles/services/styles.service';
import { StyleStateEnum } from '../../../../../Common/PageBuilder/enums/style-state.enum';
import { Template } from '../../../../../Common/Styles/models/template.model';
import { FullProfileDialogAnimationsEnum } from '../../../../../Core/Dialogs/enums/full-profile-dialog-animations.enum';
import { OverlayAnimationStateEnum } from '../../../../../Core/Dialogs/enums/overlay-animation.enum';
import { FullProfileAnimationModel } from '../../../../../Core/Dialogs/models/full-profile-animation.model';
import { DialogIoModel } from '../../../../../Core/Dialogs/models/dialog-io.model';
import { BlockDisplayTypeEnum } from '../../../enums/block-display-type.enum';
import { dividerColorConfig, backgroundColorConfig, AdvancedColorPickerFieldModel } from '@Common/PageBuilder/models/advanced-color-picker-field.model';


@Component({
    selector: 'agpr-divider-style-field',
    templateUrl: './divider-style-field.component.html',
    styleUrls: ['./divider-style-field.component.scss'],
    providers: [
        {
            provide: AbstractBaseStyleFieldComponent,
            useExisting: forwardRef(() => DividerStyleFieldComponent)
        }
    ],
    standalone: false
})
export class DividerStyleFieldComponent extends AbstractBaseStyleFieldComponent implements OnInit, OnChanges, OnDestroy {
    @Input() declare value: DividerStyleModel;
    @Input() declare field: DividerStyleFieldModel;
    @Output() changes: EventEmitter<DividerStyleModel> = new EventEmitter<DividerStyleModel>();

    public dividerColorField: AdvancedColorPickerFieldModel;
    public styleStateField: DropdownFieldModel;
    public backgroundColorField: AdvancedColorPickerFieldModel;

    readonly messageSeverityEnum = messageSeverityEnum;
    readonly buttonTypes = ButtonTypesEnum;

    protected defaultStyleStateOptions: DropdownOptionModel[] = [
        { label: 'PageBuilder.formField.styles.defaultAgilityStyle', value: StyleStateEnum.DEFAULT },
        { label: 'PageBuilder.formField.styles.custom', value: StyleStateEnum.CUSTOM }
    ];
    protected subscriptions: Subscription[] = [];
    protected styles: Style[];

    constructor(
        protected coreDialogService: CoreDialogService,
        protected stylesService: StylesService,
        public abstractPageBuilderService: AbstractPageBuilderService
    ) {
        super(coreDialogService);
        this.subscriptions.push(
            this.stylesService.all$.pipe(
                filter(x => !!x),
            ).subscribe(styles => {
                this.styles = styles;

                this.updateStyleOptions();
                this.updateStyleFromSavedStyle();
            })
        );
    }

    ngOnInit() {
        if (this.block?.displayElement) {
            this.field.allowExpertMode = this.block?.displayElement?.displayType !== BlockDisplayTypeEnum.ONLINE;
        }

        if (!this.value) {
            this.value = new DividerStyleModel();
        }

        if (this.field?.defaultStyleStateOptions) {
            this.defaultStyleStateOptions = this.field.defaultStyleStateOptions;
        }
        if (this.field.changeDefaultStyleStateOptions) {
            this.defaultStyleStateOptions = this.field.changeDefaultStyleStateOptions(this);
        }
        this.styleStateField = new DropdownFieldModel(
            null,
            'displayStyleState',
            'PageBuilder.styles.contentBlockStyle',
            '',
            this.defaultStyleStateOptions
        );
        this.updateStyleOptions();



        this.dividerColorField = AdvancedColorPickerFieldModel.create({
            ...dividerColorConfig(true),
            key: 'color',
            label: 'PageBuilder.formField.dividerColorLabel',
            tooltip: ''
        });



        this.backgroundColorField = AdvancedColorPickerFieldModel.create({
            ...backgroundColorConfig(true),
            label: 'Common.forms.richText.backgroundColor',
            tooltip: ''
        });
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.hasOwnProperty('value')) {
            if (!this.value) {
                this.value = new DividerStyleModel();
            }
            if (!this.value.height) {
                this.value.height = this.field.defaults.height;
            }

            if (!this.value.dividerColor) {
                this.value.dividerColor = this.field.defaults.dividerColor;
            }
            if (!this.value.backgroundColor) {
                this.value.backgroundColor = this.field.defaults.backgroundColor;
            }

            if (!this.value.padding){
                this.value.padding = this.field.defaults.padding;
            }
        }
    }

    public saveStyle(): void {
        if (!this.styleModel) {
            this.styleModel = new Style();
        }

        this.styleModel.style = this.value;

        const io: DialogIoModel = {
            inputs: {
                type: this.field.styleType,
                inputStyle: this.styleModel
            },
            outputs: {
                dialogClose: (response) => {
                    if (response) {
                        this.setStyle(response, 'displayStyleState', true);
                    }
                }
            }
        };

        this.coreDialogService.open(SaveStylesDialogComponent, io);
    }

    /**
     * Opens the save-twig-template-dialog after loading the data needed for it
     */
    public saveExpertTemplate(): void {
        if (!this.styleModel) {
            this.styleModel = new Style();
        }

        this.styleModel.style = this.value;
        this.styleModel.template = this.value.template;
        this.saveTwigTemplateDialogModel.inputs.defaultTemplate = this.abstractPageBuilderService.getDefaultTemplateByBlockType(
            this.abstractPageBuilderService.getBlockTypeEnumFromStyleTypeEnum(this.field.styleType),
            StyleStateEnum.DEFAULT
        );
        // Set the default template if there's not a template on this style already
        if (!this.styleModel.template) {
            this.setTemplate = true;
            this.styleModel.template = new Template();
            this.styleModel.template.template = this.saveTwigTemplateDialogModel.inputs.defaultTemplate;
        }

        this.saveTwigTemplateDialogModel.inputs.type = this.field.styleType;
        this.saveTwigTemplateDialogModel.inputs.inputStyle = this.styleModel;
        const dialogKey = this.coreDialogService.open(SaveTwigTemplateDialogComponent, this.saveTwigTemplateDialogModel);
    }

    public manageStylesButtonClicked(): void {
        const overlayContainer = document.getElementById('main-content');
        const animationTrigger = new BehaviorSubject(
            new FullProfileAnimationModel(
                FullProfileDialogAnimationsEnum.slideInOutAnimation,
                OverlayAnimationStateEnum.open
            )
        );
        const io = {
            inputs: {
                animationTrigger: animationTrigger,
                overlayContainer: overlayContainer
            }
        };
        this.coreDialogService.open(ManageStylesDialogComponent, io);
    }

    protected updateStyleFromSavedStyle() {
        if (!this.value || !this.styles || !Number(this.value.styleState)) {
            return;
        }
        const savedStyle: Style = this.styles.find(x => x.id === this.value.styleState as number);
        if (!savedStyle) {
            // the list doesn't contain the id we're looking for, can't load it
            return;
        }
        const dividerStyleModel = new DividerStyleModel(savedStyle.style);
        dividerStyleModel.styleState = this.value.styleState;
        dividerStyleModel.displayStyleState = this.value.displayStyleState;
        dividerStyleModel.template = savedStyle.template;

        this.value = dividerStyleModel;
    }

    protected updateStyleOptions(): void {
        if (!this.styles || !this.value || !this.value.styleState) {
            return;
        }

        if (this.field.changeDefaultStyleStateOptions) {
            this.defaultStyleStateOptions = this.field.changeDefaultStyleStateOptions(this);
        }

        const styleDropdownOptions = this.styles
            .filter(style => style.type === this.field.styleType)
            .map(
                style => {
                    return {
                        label: style.name,
                        value: style.id
                    };
                }
            );

        this.styleStateField.options = [
            ...this.defaultStyleStateOptions,
            ...styleDropdownOptions
        ];
    }

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
}

